import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack,
} from '@mui/icons-material';
import { DangerTriangle,CheckCircle } from "@mynaui/icons-react";

const PartsAvailabilityModal = ({ open, onClose, onComplete, healthData }) => {
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (open) {
      setChecking(true);
      // Simulate checking inventory for 2 seconds
      const timer = setTimeout(() => {
        setChecking(false);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [open]);

  const handleProceed = () => {
    onComplete(healthData.partsAvailable);
  };

  const renderCheckingScreen = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        py: 6,
      }}
    >
      <CircularProgress size={60} sx={{ mb: 3, color: '#2E7D32' }} />
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
        Fetching Inventory
      </Typography>
      <Typography variant="body1" sx={{ color: '#757575' }}>
        Verifying parts availability
      </Typography>
    </Box>
  );

  const renderPartsNotAvailable = () => {
    const outOfStockParts = healthData.requiredParts.filter(
      (part) => part.status === 'out_of_stock'
    );
    const availableParts = healthData.requiredParts.filter(
      (part) => part.status === 'available'
    );

    return (
      <Box>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <DangerTriangle style={{ fontSize: 50, color: '#C62828' }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Parts Not Available
          </Typography>
          <Typography variant="body2" sx={{ color: '#757575' }}>
            Some required parts are out of stock
          </Typography>
        </Box>

        {/* Out of Stock Parts */}
        {outOfStockParts.map((part, index) => (
          <Box
            key={`out-${index}`}
            sx={{
              border: '1px solid #FFCDD2',
              borderRadius: '12px',
              p: 2,
              mb: 2,
              backgroundColor: '#FFEBEE',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 600, color: '#C62828', mb: 0.5 }}
                >
                  {part.name}
                </Typography>
                <Typography variant="caption" sx={{ color: '#757575' }}>
                  {part.partNumber}
                </Typography>
              </Box>
              <Box
                sx={{
                  backgroundColor: '#C62828',
                  color: '#fff',
                  px: 2,
                  py: 0.5,
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                }}
              >
                Out Of Stock
              </Box>
            </Box>
          </Box>
        ))}

        {/* Available Parts */}
        {availableParts.map((part, index) => (
          <Box
            key={`avail-${index}`}
            sx={{
              border: '1px solid #E0E0E0',
              borderRadius: '12px',
              p: 2,
              mb: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                {part.name}
              </Typography>
              <Typography variant="caption" sx={{ color: '#757575' }}>
                {part.partNumber}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Qty : {part.quantity}
              </Typography>
              <Typography variant="caption" sx={{ color: '#757575' }}>
                {part.shelf}
              </Typography>
            </Box>
          </Box>
        ))}

        {/* Request ID */}
        {healthData.requestId && (
          <Box
            sx={{
              border: '1px solid #C8E6C9',
              borderRadius: '12px',
              p: 2,
              mb: 3,
              backgroundColor: '#E8F5E9',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <DangerTriangle style={{ color: '#2E7D32', fontSize: 20 }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#2E7D32' }}>
                {healthData.requestId} Created by Agent
              </Typography>
              <Typography variant="caption" sx={{ color: '#2E7D32' }}>
                Sent for Supervisor approval
              </Typography>
            </Box>
          </Box>
        )}

        <Button
          fullWidth
          variant="contained"
          startIcon={<ArrowBack />}
          onClick={onClose}
          sx={{
            backgroundColor: '#212121',
            color: '#fff',
            textTransform: 'none',
            fontWeight: 600,
            py: 1.5,
            '&:hover': {
              backgroundColor: '#000',
            },
          }}
        >
          Check back later
        </Button>
      </Box>
    );
  };

  const renderPartsAvailable = () => {
    return (
      <Box>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <CheckCircle style={{ fontSize: 50, color: '#2E7D32' }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            All Parts Available
          </Typography>
          <Typography variant="body2" sx={{ color: '#757575' }}>
            Ready to proceed with Service
          </Typography>
        </Box>

        {/* Available Parts List */}
        {healthData.requiredParts.map((part, index) => (
          <Box
            key={index}
            sx={{
              border: '1px solid #E0E0E0',
              borderRadius: '12px',
              p: 2,
              mb: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                {part.name}
              </Typography>
              <Typography variant="caption" sx={{ color: '#757575' }}>
                {part.partNumber}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Qty : {part.quantity}
              </Typography>
              <Typography variant="caption" sx={{ color: '#757575' }}>
                {part.shelf}
              </Typography>
            </Box>
          </Box>
        ))}

        <Button
          fullWidth
          variant="contained"
          onClick={handleProceed}
          sx={{
            backgroundColor: '#212121',
            color: '#fff',
            textTransform: 'none',
            fontWeight: 600,
            py: 1.5,
            mt: 2,
            '&:hover': {
              backgroundColor: '#000',
            },
          }}
        >
          ▷ Proceed with next steps
        </Button>
      </Box>
    );
  };

  const renderContent = () => {
    if (checking) {
      return renderCheckingScreen();
    }

    if (!healthData.partsAvailable) {
      return renderPartsNotAvailable();
    }

    return renderPartsAvailable();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          p: 3,
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>{renderContent()}</DialogContent>
    </Dialog>
  );
};

export default PartsAvailabilityModal;